import { useState } from "react";
import { AvatarPersona } from "@/components/ui/avatar-persona";
import { useFotoPerfilVista } from "@/hooks/useFotoPerfilVista";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Inbox,
  ClipboardCheck,
  Workflow,
  FileOutput,
  HandCoins,
  Users,
  CheckCircle2,
  FileText,
  FileCheck2,
  GitMerge,
  BarChart3,
  Percent,
  Receipt,
  Menu,
  LucideIcon,
} from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useAuth } from "@/contexts/AuthContext";
import { useCanReturnToAdmin } from "@/hooks/useCanReturnToAdmin";
import { useAllowedMenus } from "@/hooks/useAllowedMenus";
import { PortalSidebar, PORTAL_SIDEBAR_ASIDE_CLS, PORTAL_SIDEBAR_SHEET_CLS } from "@/components/admin/PortalSidebar";
import { AdministracionFiltersProvider } from "@/contexts/AdministracionFiltersContext";
import { PortalTrackingProvider } from "@/contexts/PortalTrackingContext";
import { usePortalNavEstado, type PortalNavItem } from "@/hooks/usePortalNav";
import { GlobalFilterBar } from "./GlobalFilterBar";

const ROUTES_SIN_FILTER_BAR = [
  "/admin/portal-administracion/bandeja-ejecucion",
  "/admin/portal-administracion/ciclo-venta",
  "/admin/portal-administracion/facturas-propiedades",
  "/admin/portal-administracion/facturas-por-cobrar",
  "/admin/portal-administracion/facturas-por-pagar",
  "/admin/portal-administracion/comisiones-externas",
  "/admin/portal-administracion/comisiones-internas",
  "/admin/portal-administracion/pagos-ejecutados",
  "/admin/portal-administracion/cfdis-emitidos",
  "/admin/portal-administracion/facturas",
  "/admin/portal-administracion/conciliacion-stp",
  "/admin/portal-administracion/configuracion-fiscal",
  "/admin/portal-administracion/reportes",
];

/*
 * El menú lateral vive en BD: `submenus` del menú 23 (etiqueta, orden y activo), filtrado por
 * el permiso del rol. Para agregar, quitar o renombrar una sección se cambia la BD, no este
 * archivo. Aquí solo quedan el ícono y el grupo visual de cada ruta; una ruta sin grupo cae
 * en "Otros" para que nunca desaparezca del menú.
 */
const ADMINISTRACION_MENU_ID = 23;

const iconMap: Record<string, LucideIcon> = {
  "/admin/portal-administracion/bandeja-ejecucion":    Inbox,
  "/admin/portal-administracion/bandeja":              ClipboardCheck,
  "/admin/portal-administracion/ciclo-venta":          Workflow,
  "/admin/portal-administracion/facturas-propiedades": Receipt,
  "/admin/portal-administracion/facturas-por-cobrar":  FileOutput,
  "/admin/portal-administracion/facturas-por-pagar":   Inbox,
  "/admin/portal-administracion/comisiones-externas":  HandCoins,
  "/admin/portal-administracion/comisiones-internas":  Users,
  "/admin/portal-administracion/pagos-ejecutados":     CheckCircle2,
  "/admin/portal-administracion/facturas":             FileCheck2,
  "/admin/portal-administracion/cfdis-emitidos":       FileText,
  "/admin/portal-administracion/conciliacion-stp":     GitMerge,
  "/admin/portal-administracion/configuracion-fiscal": Percent,
  "/admin/portal-administracion/reportes":             BarChart3,
};

const GRUPOS = ["Ejecución", "Pagos y Cobranza", "Historial", "Administración"] as const;
const GRUPO_OTROS = "Otros";

const grupoPorRuta: Record<string, (typeof GRUPOS)[number]> = {
  "/admin/portal-administracion/bandeja-ejecucion":    "Ejecución",
  "/admin/portal-administracion/bandeja":              "Ejecución",
  "/admin/portal-administracion/ciclo-venta":          "Ejecución",
  "/admin/portal-administracion/facturas-propiedades": "Ejecución",
  "/admin/portal-administracion/facturas-por-cobrar":  "Pagos y Cobranza",
  "/admin/portal-administracion/facturas-por-pagar":   "Pagos y Cobranza",
  "/admin/portal-administracion/comisiones-externas":  "Pagos y Cobranza",
  "/admin/portal-administracion/comisiones-internas":  "Pagos y Cobranza",
  "/admin/portal-administracion/pagos-ejecutados":     "Historial",
  "/admin/portal-administracion/facturas":             "Historial",
  "/admin/portal-administracion/cfdis-emitidos":       "Historial",
  "/admin/portal-administracion/conciliacion-stp":     "Historial",
  "/admin/portal-administracion/configuracion-fiscal": "Administración",
  "/admin/portal-administracion/reportes":             "Administración",
};

interface NavGroup {
  label: string;
  items: PortalNavItem[];
}

export const PortalAdministracionLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { canReturnToAdmin } = useCanReturnToAdmin();
  const { items: navFromDb, cargando: navCargando } = usePortalNavEstado(ADMINISTRACION_MENU_ID, iconMap, FileText);
  const { isPathAllowed, isLoading: permisosCargando } = useAllowedMenus();
  const cargandoNav = navCargando || permisosCargando;

  // Items de BD (ya en su orden) permitidos al rol, repartidos en los grupos visuales.
  // `isPathAllowed` ya descarta lo apagado en BD (submenú o menú padre activo=false).
  const permitidos = navFromDb.filter((item) => isPathAllowed(item.path));
  const visibleNavGroups: NavGroup[] = [...GRUPOS, GRUPO_OTROS]
    .map((label) => ({
      label,
      items: permitidos.filter((item) => (grupoPorRuta[item.path] ?? GRUPO_OTROS) === label),
    }))
    .filter((group) => group.items.length > 0);

  const handleNavigate = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const currentSection = (() => {
    for (const g of visibleNavGroups) {
      for (const item of g.items) {
        if (isActive(item.path)) return item.label;
      }
    }
    return "Administración";
  })();

  const rawName = profile?.nombre || profile?.email?.split("@")[0] || "Usuario";
  const userName = rawName.trim().split(/\s+/).slice(0, 2).join(" ");
  const userRole = profile?.rol_nombre ?? "Administración";
  const initials = userName.split(" ").filter(Boolean).slice(0, 2).map((p: string) => p.charAt(0).toUpperCase()).join("") || "U";
  const fotoVista = useFotoPerfilVista();

  const sidebar = (
    <PortalSidebar
      portal="Portal Administración"
      cargando={cargandoNav}
      groups={visibleNavGroups.map((group) => ({
        label: group.label,
        items: group.items.map((item) => ({
          key: item.path,
          label: item.label,
          icon: item.icon,
          active: isActive(item.path),
          onSelect: () => handleNavigate(item.path),
        })),
      }))}
      usuario={{ nombre: userName, rol: userRole, iniciales: initials }}
      onRegresar={canReturnToAdmin ? () => handleNavigate("/admin") : undefined}
      onCerrarSesion={() => signOut()}
    />
  );

  return (
    <PortalTrackingProvider portal="admin">
      <AdministracionFiltersProvider>
        <div className="min-h-screen flex antialiased">
          {/* Desktop sidebar */}
          <aside className={PORTAL_SIDEBAR_ASIDE_CLS}>
            {sidebar}
          </aside>

          {/* Mobile drawer */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetContent side="left" className={PORTAL_SIDEBAR_SHEET_CLS}>
              {sidebar}
            </SheetContent>
          </Sheet>

          <div className="flex-1 lg:pl-64 min-w-0">
            {/* Mobile header */}
            <header className="flex lg:hidden flex-col sticky top-0 z-20 bg-card border-b border-border">
              <div className="flex items-center px-4 pt-3 pb-2 gap-3">
                <button
                  onClick={() => setMobileOpen(true)}
                  className="p-1.5 -ml-1 rounded-md text-foreground hover:bg-muted transition-colors"
                  aria-label="Abrir menú"
                >
                  <Menu className="h-5 w-5" />
                </button>
                <div className="min-w-0 flex-1">
                  <p className="text-[15px] font-semibold text-foreground tracking-tight truncate">{currentSection}</p>
                </div>
                <AvatarPersona nombre={userName} fotoUrl={fotoVista} size="sm" />
              </div>
            </header>

            <main className="px-8 py-4 bg-background min-h-screen">
              {!ROUTES_SIN_FILTER_BAR.some((r) => location.pathname.startsWith(r)) && (
                <GlobalFilterBar />
              )}
              <Outlet />
            </main>
          </div>
        </div>
      </AdministracionFiltersProvider>
    </PortalTrackingProvider>
  );
};

export default PortalAdministracionLayout;
